import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { WineItem } from '../types';
import { fetchWine, addWine, deleteWine, updateWine, scanWine, parseWineInput } from '../services/api';

interface WineState {
    wineCellar: WineItem[];
    wineDraft: WineItem[];
    editingWine: WineItem | null;
    isSavingWine: boolean;
    isWineScanning: boolean;
    wineUpdateLoading: number | null;
    newWine: string;
    isAddingWine: boolean;
}

interface WineActions {
    loadWine: (token: string) => Promise<void>;
    setNewWine: (name: string) => void;
    setEditingWine: (wine: WineItem | null) => void;
    setWineDraft: (draft: WineItem[]) => void;
    setWineCellar: (items: WineItem[]) => void;
    handleAddWine: (token: string) => Promise<WineItem[]>;
    handleDeleteWine: (token: string, id: number) => Promise<WineItem[]>;
    handleSaveWine: (token: string, updatedData: WineItem) => Promise<WineItem[]>;
    handleStockCellar: (token: string) => Promise<WineItem[]>;
    handleWineScan: (token: string, file: File) => Promise<number>;
    removeFromDraft: (index: number) => void;
    reset: () => void;
}

interface WineStore extends WineState, WineActions { }

const initialState: WineState = {
    wineCellar: [],
    wineDraft: [],
    editingWine: null,
    isSavingWine: false,
    isWineScanning: false,
    wineUpdateLoading: null,
    newWine: "",
    isAddingWine: false,
};

export const useWineStore = create<WineStore>()(
    devtools(
        (set, get) => ({
            ...initialState,

            reset: () => set(initialState),

            setNewWine: (name) => set({ newWine: name }),
            setEditingWine: (wine) => set({ editingWine: wine }),
            setWineDraft: (draft) => set({ wineDraft: draft }),
            setWineCellar: (items) => set({ wineCellar: items }),

            loadWine: async (token) => {
                try {
                    const items = await fetchWine(token);
                    set({ wineCellar: items });
                } catch (err) {
                    console.error("Failed to load wine:", err);
                }
            },

            handleAddWine: async (token) => {
                const { newWine } = get();
                if (!newWine.trim()) return;

                set({ isAddingWine: true });
                try {
                    let wineToAdd: WineItem;
                    try {
                        wineToAdd = await parseWineInput(token, newWine);
                    } catch (err) {
                        console.warn("Wine parsing failed, using fallback", err);
                        wineToAdd = {
                            name: newWine,
                            varietal: 'Unknown',
                            region: 'Unknown',
                            vintage: null,
                            wine_type: 'Unknown',
                            quantity: 1,
                            is_available: true
                        };
                    }

                    await addWine(token, wineToAdd);
                    const items = await fetchWine(token);
                    set({ wineCellar: items, newWine: "" });
                    return items;
                } catch (err) {
                    console.error("Failed to add wine:", err);
                    throw err;
                } finally {
                    set({ isAddingWine: false });
                }
            },

            handleDeleteWine: async (token: string, id: number) => {
                set({ wineUpdateLoading: id });
                try {
                    await deleteWine(token, id);
                    const items = get().wineCellar.filter(w => w.id !== id);
                    set({ wineCellar: items });
                    return items;
                } catch (err) {
                    console.error("Delete failed:", err);
                    throw err;
                } finally {
                    set({ wineUpdateLoading: null });
                }
            },

            handleSaveWine: async (token, updatedData) => {
                const { editingWine } = get();
                set({ isSavingWine: true });
                try {
                    if (updatedData.id) {
                        await updateWine(token, updatedData.id, updatedData);
                        const items = get().wineCellar.map(w => w.id === updatedData.id ? { ...w, ...updatedData } : w);
                        set({ wineCellar: items });
                    } else {
                        // Updating an item in the draft
                        set((state) => ({
                            wineDraft: state.wineDraft.map(w => w === editingWine ? updatedData : w)
                        }));
                    }
                    set({ editingWine: null });
                    return get().wineCellar;
                } catch (err) {
                    console.error("Failed to save wine:", err);
                    throw err;
                } finally {
                    set({ isSavingWine: false });
                }
            },

            handleStockCellar: async (token) => {
                const { wineDraft } = get();
                if (wineDraft.length === 0) return;

                try {
                    for (const wine of wineDraft) {
                        await addWine(token, wine);
                    }
                    const items = await fetchWine(token);
                    set({ wineCellar: items, wineDraft: [] });
                    return items;
                } catch (err) {
                    console.error("Cellar commitment failed:", err);
                    throw err;
                }
            },

            handleWineScan: async (token, file) => {
                set({ isWineScanning: true });
                try {
                    const result = await scanWine(token, file);
                    if (result.detected && result.detected.length > 0) {
                        set((state) => ({
                            wineDraft: [...state.wineDraft, ...result.detected]
                        }));
                        return result.detected.length;
                    }
                    return 0;
                } catch (err) {
                    console.error("Wine scan failed:", err);
                    throw err;
                } finally {
                    set({ isWineScanning: false });
                }
            },

            removeFromDraft: (index) => {
                set((state) => ({
                    wineDraft: state.wineDraft.filter((_, i) => i !== index)
                }));
            }
        }),
        { name: 'WineStore' }
    )
);
